import { ApolloProvider } from '@apollo/client'
import { getTaskByType } from '@components/mewoz/Tasks/helpers'
import { PropertyContextProps } from '@context/types'
import {
  Case,
  CasesQuery,
  PostCaseInput,
  Profile,
  Task,
  TaskType,
  useBindCaseLazyQuery,
  useCasesLazyQuery,
  useGetUserDataLazyQuery,
  usePostCaseLazyQuery,
} from '@graphql/generated'
import pushToDataLayer from '@logic/data-layers/pushToDataLayer'
import { routes } from '@routes/index'
import getDistinctYears from '@utils/getDistinctYears'
import { isBrowser } from '@utils/isBrowser'
import { isUserLoggedIn } from '@utils/isUserLoggedIn'
import redirectService from '@utils/services/redirectServices'
import tokenService from '@utils/services/tokenServices'
import { navigate } from 'gatsby'
import { useStateMachine } from 'little-state-machine'
import * as React from 'react'
import { ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react'
import { client } from '../apolloClient/client'

const defaultValues = {
  selectorOpen: false,
  setSelectorOpen: () => undefined,
  userData: null,
  setUserData: (_data: Profile) => undefined,
  logout: (_callback: () => void) => undefined,
  activeCaseRef: null,
  setActiveCaseRef: () => undefined,
  allProperties: [],
  activeProperty: undefined,
  fetchCases: () => new Promise(resolve => resolve({})),
  refetchUserData: () => new Promise(resolve => resolve({})),
  casesLoading: false,
  isLoading: true,
  setIsLoading: (_value: boolean) => undefined,
  canNavigate: true,
  setCanNavigate: (_value: boolean) => undefined,
  blockedLink: null,
  setBlockedLink: (_value: string | null) => undefined,
}

export const PropertyContext = createContext<PropertyContextProps>(defaultValues)

const ACTIVE_CASE_REF = 'activeCaseReference'

export const PropertyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [canNavigate, setCanNavigate] = useState(true)
  const [blockedLink, setBlockedLink] = useState<string | null>(null)
  const [getCases, { loading: casesLoading, data: casesData, error }] = useCasesLazyQuery()
  const [getUserData, { data: userData, client, updateQuery: updateUserDataQuery }] = useGetUserDataLazyQuery()
  const activeCaseRefStorage = isBrowser ? localStorage.getItem(ACTIVE_CASE_REF) : null
  const [selectorOpen, setSelectorOpen] = useState(false)
  const [activeCaseRef, setActiveCaseRef] = useState<string | null>(activeCaseRefStorage)
  const [registeredCaseRef, setRegisteredCaseRef] = useState<string | null>(null)
  const [allProperties, setAllProperties] = useState<Case[]>([])
  const [bindCase, {}] = useBindCaseLazyQuery()
  const [postCase, {}] = usePostCaseLazyQuery()
  const { state } = useStateMachine()
  const isMewoz = isBrowser && window.location.pathname.includes('/mijn-eerlijke-woz')
  const [isRegistered, setIsRegistered] = useState(false)

  useEffect(() => {
    if (activeCaseRef) {
      localStorage.setItem(ACTIVE_CASE_REF, activeCaseRef)
    } else {
      localStorage.removeItem(ACTIVE_CASE_REF)
    }
  }, [activeCaseRef])

  useEffect(() => {
    if (error?.message === 'failed to decode token') {
      void navigate(routes.signOut)
    }
  }, [error])

  const fetchCases = (callback?: (data?: CasesQuery['cases']) => void) =>
    getCases({ fetchPolicy: 'no-cache' }).then(data => {
      callback && callback(data?.data?.cases)
    })

  const refetchUserData = async () => {
    try {
      await getUserData({ fetchPolicy: 'network-only' })
    } catch (error) {
      console.error('Failed to refetch user data:', error)
    }
  }

  useEffect(() => {
    if (casesData?.cases?.cases) {
      setAllProperties(casesData.cases.cases)
    }
  }, [casesData])

  useEffect(() => {
    const userFlow = redirectService.getUserFlow()

    if (!registeredCaseRef || !userFlow || !!isRegistered) return

    if (state?.warehouse?.classification === 'green')
      pushToDataLayer({
        event: 'interaction',
        event_name: 'registration_green_completed',
        user_flow: userFlow,
        case_ref: registeredCaseRef,
        savings_avg: state?.warehouse?.valuation?.savings?.avg,
        ewoz_value: state?.warehouse?.valuation?.['ewoz-value'],
        woz_value: state?.warehouse?.['property-details']?.['woz-value'],
      })

    if (state?.warehouse?.classification === 'orange')
      pushToDataLayer({
        event: 'interaction',
        event_name: 'registration_orange_completed',
        user_flow: userFlow,
        case_ref: registeredCaseRef,
        savings_avg: state?.warehouse?.valuation?.savings?.avg,
        ewoz_value: state?.warehouse?.valuation?.['ewoz-value'],
        woz_value: state?.warehouse?.['property-details']?.['woz-value'],
      })

    // GENERIC METRIC
    pushToDataLayer({
      event: 'interaction',
      event_name: 'registration_completed',
      user_flow: userFlow,
      case_ref: registeredCaseRef,
      classification: state?.warehouse?.classification,
      savings_avg: state?.warehouse?.valuation?.savings?.avg,
      ewoz_value: state?.warehouse?.valuation?.['ewoz-value'],
      woz_value: state?.warehouse?.['property-details']?.['woz-value'],
    })

    setIsRegistered(true)
  }, [registeredCaseRef, allProperties])

  useEffect(() => {
    const activeProperty = allProperties?.find(property => property.caseRef === activeCaseRef)
    if (!isLoading && !activeProperty) {
      const years = getDistinctYears(allProperties)
      const defaultActiveProperty = allProperties.find(property => property.taxYear === years[0])

      if (defaultActiveProperty) {
        setActiveCaseRef(defaultActiveProperty.caseRef)
      }
    }

    allProperties.find(property => property.caseRef === activeCaseRef)
  }, [isLoading, activeCaseRef, allProperties])

  const maybeBindCase = async (callback: () => void) => {
    const caseReference = redirectService.getCaseReference()
    const userFlow = redirectService.getUserFlow()
    const dmDossier = redirectService.getDmDossier()
    const emailDossier = redirectService.getEmailDossier()

    if (caseReference && userFlow) {
      let payload: PostCaseInput = {
        userFlow: userFlow,
      }

      if (state.warehouse?.['case-ref'] === caseReference) {
        payload = { ...payload, wozCheck: state.user?.['woz-check'] }
      }

      if (dmDossier) {
        payload['directMail'] = true
      }

      if (emailDossier) {
        payload['fromEmail'] = true
      }

      try {
        await bindCase({ variables: { caseRef: caseReference } })
        try {
          await postCase({
            variables: {
              caseRef: caseReference,
              input: payload,
            },
          })
          setRegisteredCaseRef(caseReference)
          callback()
        } catch (e) {
          window.Rollbar?.error(`Failed to post the case ${caseReference}`, error)
        }
      } catch (e) {
        window.Rollbar?.error(`Failed to bind the case ${caseReference}`, error)
      }
    } else {
      callback()
    }
  }

  useEffect(() => {
    if (isUserLoggedIn() && isMewoz) {
      // Fetch user data, maybe add to the below promise?
      void getUserData()

      const caseReference = redirectService.getCaseReference()
      if (caseReference) setActiveCaseRef(caseReference)

      void maybeBindCase(
        () =>
          void fetchCases(cases => {
            if (!cases) return setIsLoading(false)

            redirectService.removeCaseReference()
            redirectService.removeDmDossier()
            redirectService.removeEmailDossier()
            redirectService.removeUserFlow()
            redirectService.maybeDoRedirect(() => {
              const active = caseReference || activeCaseRef
              const tasks = (cases?.cases as Case[])?.find(property => property.caseRef === active)?.tasks as Task[]

              const openPoa = redirectService.getOpenPoa() || redirectService.getTaskType() === TaskType.PoaMissing

              if (openPoa) {
                const poaTask = tasks ? getTaskByType(tasks, TaskType.PoaMissing) : null
                if (poaTask && poaTask.status === 'pending')
                  void navigate(`${routes.mewoz.tasks.poaMissing}?id=${poaTask.taskId}`)
                redirectService.removeOpenPoa()
                redirectService.removeTaskType()
              }

              switch (redirectService.getTaskType()) {
                case TaskType.Photo:
                  const photoTask = getTaskByType(tasks, TaskType.Photo)
                  if (photoTask)
                    void navigate(`${routes.mewoz.task.replace(':taskId', photoTask.taskId)}?method=deeplink`)
                  break
                case TaskType.ExtraDetails:
                  const extraDetailsTask = getTaskByType(tasks, TaskType.ExtraDetails)
                  if (extraDetailsTask && extraDetailsTask.status === 'pending')
                    void navigate(`${routes.mewoz.tasks.extraDetails}?id=${extraDetailsTask.taskId}&method=deeplink`)
                  break
              }

              redirectService.removeTaskType()
            })

            setIsLoading(false)
          })
      )
    }
  }, [])

  const memoedValue = useMemo(
    () => ({
      selectorOpen,
      setSelectorOpen,
      canNavigate,
      setCanNavigate,
      blockedLink,
      setBlockedLink,
      userData: userData?.getUserData as Profile,
      setUserData: (newData: Profile) => {
        updateUserDataQuery(_ => ({
          getUserData: newData,
        }))
      },
      logout: async (callback: () => void) => {
        setIsLoading(true)

        try {
          if (isUserLoggedIn()) {
            await client.resetStore()
          }
        } catch (e) {
          window.Rollbar?.error(`Logout error`, e)
        }

        tokenService.removeToken()
        setIsLoading(false)
        callback && callback()
      },
      activeCaseRef,
      setActiveCaseRef,
      allProperties,
      activeProperty: allProperties.find(property => property.caseRef === activeCaseRef),
      fetchCases,
      refetchUserData,
      casesLoading,
      isLoading,
      setIsLoading,
    }),
    [blockedLink, canNavigate, selectorOpen, userData, activeCaseRef, allProperties, isLoading, casesLoading]
  )

  return <PropertyContext.Provider value={memoedValue}>{children}</PropertyContext.Provider>
}

export const withPropertyProvider =
  <P extends object>(Component: React.ComponentType<P>) =>
  (props: P) =>
    (
      <ApolloProvider client={client}>
        <PropertyProvider>
          <Component {...props} />
        </PropertyProvider>
      </ApolloProvider>
    )

export const usePropertyData = () => useContext(PropertyContext)
